import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  DataGridPremium,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Typography,
  Dialog,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import AUTH_CONTEXT from "../../store";
import { PhysioServices } from "../../services/physio_services";
import { BASE_PATH_TO_HOME } from "../../base_routes_paths/data_paths";
import classes from "./Physio.module.css";

const PhysioList = () => {
  const { token } = AUTH_CONTEXT();
  const navigate = useNavigate();

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [data, setData] = useState([]);
  const [selectedPhysio, setSelectedPhysio] = useState(null);
  const [selectedPhysioId, setSelectedPhysioId] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [monthValidationErrors, setMonthValidationErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  const isFetchingRef = useRef(false);

  // Edit form states
  const [editForm, setEditForm] = useState({
    Status: "",
    IsActive: false,
    IsDeleted: false,
    ShowSubscription: false,
    SubscriptionValidityInDays: {},
    CreditPriceWithoutTax: '',
  });

  const [creditsForm, setCreditsForm] = useState({
    Credits: "",
    ResellerID: "",
    SalesExecutiveID: "",
    AmountWithoutTax: "",
    IsPaid: false,
  });

  // Dropdown data states
  const [resellerList, setResellerList] = useState([]);
  const [salesExecutiveList, setSalesExecutiveList] = useState([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false);

  const fetchPhysioList = useCallback(async () => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      console.log("fetchPhysioList called but already fetching, skipping...");
      return;
    }

    console.log("fetchPhysioList called");
    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      const result = await PhysioServices.getAllPhysioList(token, navigate);

      if (result.status === 200) {
        const physioData = result.data.PhysioList.map((item, index) => ({
          id: item.PhysioUserID._id,
          ...item.PhysioUserID,
          ...item,
          index: index + 1,
        }));
        setData(physioData);
        setIsDataFetched(true);
        toast.success("Physio list fetched successfully!", {
          position: "bottom-left",
          autoClose: 2000,
        });
      }
    } catch (error) {
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [token, navigate]);

  // Fetch reseller list
  const fetchResellerList = useCallback(async () => {
    try {
      setIsLoadingDropdowns(true);
      const result = await PhysioServices.getResellerList(token, navigate);

      if (result.status === 200) {
        setResellerList(result.data.ResellerList || []);
      }
    } catch (error) {
      toast.error("Failed to fetch reseller list", {
        position: "bottom-left",
        autoClose: 5000,
      });
    } finally {
      setIsLoadingDropdowns(false);
    }
  }, [token, navigate]);

  // Fetch sales executive list based on selected reseller
  const fetchSalesExecutiveList = useCallback(
    async (resellerUserID) => {
      if (!resellerUserID) {
        setSalesExecutiveList([]);
        return;
      }

      try {
        setIsLoadingDropdowns(true);
        const result = await PhysioServices.getSalesExecutiveList(
          token,
          navigate,
          resellerUserID
        );

        if (result.status === 200) {
          setSalesExecutiveList(result.data.SalesExecutiveList || []);
        }
      } catch (error) {
        toast.error("Failed to fetch sales executive list", {
          position: "bottom-left",
          autoClose: 5000,
        });
      } finally {
        setIsLoadingDropdowns(false);
      }
    },
    [token, navigate]
  );

  // Handle reseller selection
  const handleResellerChange = (resellerUserID) => {
    setCreditsForm((prev) => ({
      ...prev,
      ResellerID: resellerUserID,
      SalesExecutiveID: "",
    }));

    fetchSalesExecutiveList(resellerUserID);
  };

  const fetchPhysioDetails = async (userId) => {
    try {
      // Store the user ID for later use in API calls
      console.log("Fetching physio details for UserID:", userId);
      setSelectedPhysioId(userId);

      // Fetch reseller list when opening the modal
      fetchResellerList();

      const result = await PhysioServices.getPhysioDetails(
        token,
        navigate,
        userId
      );

      if (result.status === 200) {
        setSelectedPhysio(result.data.response);
        setEditForm({
          Status: result.data.response.Status,
          IsActive: result.data.response.IsActive,
          IsDeleted: result.data.response.IsDeleted,
          ShowSubscription: result.data.response.ShowSubscription,
          SubscriptionValidityInDays: calculateDaysForPlans(
            result.data.response.SubscriptionValidityInDays || {}
          ),
          CreditPriceWithoutTax: result.data.response.CreditPriceWithoutTax && result.data.response.CreditPriceWithoutTax > 0 ? result.data.response.CreditPriceWithoutTax : '',
        });
        setIsEditMode(false);
        setOpenDetailsDialog(true);
      }
    } catch (error) {
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
      });
    }
  };

  const handleSlotsAction = (userId, name) => {
    // Open slots page in new tab with doctor name in query params
    const slotsUrl = name
      ? `/physio/slots/${userId}?name=${encodeURIComponent(name)}`
      : `/physio/slots/${userId}`;
    window.open(slotsUrl, "_blank");
  };

  const checkForZeroAmounts = () => {
    const zeroAmountPlans = [];

    Object.entries(editForm.SubscriptionValidityInDays).forEach(
      ([country, plans]) => {
        plans.forEach((plan, index) => {
          if (plan.Amount === 0 || plan.AmountWithoutTax === 0) {
            zeroAmountPlans.push({
              country,
              index: index + 1,
              month: plan.Month,
              amount: plan.Amount,
              amountWithoutTax: plan.AmountWithoutTax,
            });
          }
        });
      }
    );

    return zeroAmountPlans;
  };

  const hasZeroAmounts = (plan) => {
    return plan.Amount <= 0 || plan.AmountWithoutTax <= 0;
  };

  const calculateDaysForPlans = (subscriptionValidityInDays) => {
    const updatedPlans = {};

    Object.entries(subscriptionValidityInDays).forEach(([country, plans]) => {
      updatedPlans[country] = plans.map((plan) => ({
        ...plan,
        Days: plan.Month * 30,
      }));
    });

    return updatedPlans;
  };

  const hasAnyChanges = () => {
    const hasCreditsData = creditsForm.Credits && creditsForm.AmountWithoutTax;

    const hasEditChanges =
      editForm.Status !== selectedPhysio?.Status ||
      editForm.IsActive !== selectedPhysio?.IsActive ||
      editForm.IsDeleted !== selectedPhysio?.IsDeleted ||
      editForm.ShowSubscription !== selectedPhysio?.ShowSubscription ||
      editForm.CreditPriceWithoutTax !== (selectedPhysio?.CreditPriceWithoutTax && selectedPhysio?.CreditPriceWithoutTax > 0 ? selectedPhysio?.CreditPriceWithoutTax : '');

    const isCreditPriceValid = editForm.CreditPriceWithoutTax !== '' && editForm.CreditPriceWithoutTax !== null && editForm.CreditPriceWithoutTax !== undefined;

    const hasSubscriptionPlansChanged = () => {
      const currentPlans = editForm.SubscriptionValidityInDays || {};
      const originalPlans = selectedPhysio?.SubscriptionValidityInDays || {};
      const allCountries = new Set([
        ...Object.keys(currentPlans),
        ...Object.keys(originalPlans),
      ]);

      for (const country of allCountries) {
        const currentCountryPlans = currentPlans[country] || [];
        const originalCountryPlans = originalPlans[country] || [];

        if (currentCountryPlans.length !== originalCountryPlans.length) {
          return true;
        }

        for (let i = 0; i < currentCountryPlans.length; i++) {
          const currentPlan = currentCountryPlans[i];
          const originalPlan = originalCountryPlans[i];

          if (
            currentPlan.Month !== originalPlan.Month ||
            currentPlan.Amount !== originalPlan.Amount ||
            currentPlan.AmountWithoutTax !== originalPlan.AmountWithoutTax
          ) {
            return true;
          }
        }
      }
      return false;
    };

    return (hasCreditsData || hasEditChanges || hasSubscriptionPlansChanged()) && isCreditPriceValid;
  };

  // Combined function to handle both updating details and assigning credits
  const updatePhysioDetails = async () => {
    try {
      setIsUpdating(true);

      const hasCreditsData =
        creditsForm.Credits && creditsForm.AmountWithoutTax;

      if (hasCreditsData) {
        if (!creditsForm.Credits || !creditsForm.AmountWithoutTax) {
          toast.error("Credits and Amount Without Tax are mandatory fields", {
            position: "bottom-left",
            autoClose: 5000,
          });
          return;
        }

        console.log(
          "Assign Credits API - Using PhysioUserID:",
          selectedPhysioId
        );
        const creditsResult = await PhysioServices.assignCreditsToPhysio(
          token,
          navigate,
          {
            PhysioUserID: selectedPhysioId,
            ...creditsForm,
          }
        );

        if (creditsResult.status !== 200) {
          throw new Error("Failed to assign credits");
        }
      } else if (
        (creditsForm.Credits && !creditsForm.AmountWithoutTax) ||
        (!creditsForm.Credits && creditsForm.AmountWithoutTax)
      ) {
        toast.error("Credits and Amount Without Tax are mandatory fields", {
          position: "bottom-left",
          autoClose: 5000,
        });
        return;
      }

      const zeroAmountPlans = checkForZeroAmounts();

      if (zeroAmountPlans.length > 0) {
        const warningMessage = zeroAmountPlans
          .map((plan) => `${plan.country} Plan ${plan.index}`)
          .join("\n");

        const confirmMessage = `Warning: ${warningMessage} has zero amount(s) \n Do you want to continue with the update?`;
        if (!window.confirm(confirmMessage)) {
          return;
        }
      }

      console.log("Update API - Using UserID:", selectedPhysioId);
      const updateData = {
        UserID: selectedPhysioId,
      };

      // Only include fields that have been changed
      if (editForm.Status !== selectedPhysio?.Status) {
        updateData.Status = editForm.Status;
      }
      if (editForm.IsActive !== selectedPhysio?.IsActive) {
        updateData.IsActive = editForm.IsActive;
      }
      if (editForm.IsDeleted !== selectedPhysio?.IsDeleted) {
        updateData.IsDeleted = editForm.IsDeleted;
      }
      if (editForm.ShowSubscription !== selectedPhysio?.ShowSubscription) {
        updateData.ShowSubscription = editForm.ShowSubscription;
      }
      // Handle CreditPriceWithoutTax validation
      if (editForm.CreditPriceWithoutTax !== (selectedPhysio?.CreditPriceWithoutTax && selectedPhysio?.CreditPriceWithoutTax > 0 ? selectedPhysio?.CreditPriceWithoutTax : '')) {
        // Check if credit price is valid (not empty and greater than 0)
        if (editForm.CreditPriceWithoutTax === '' || editForm.CreditPriceWithoutTax <= 0) {
          toast.error("Credit price must be greater than 0", {
            position: "bottom-left",
            autoClose: 5000,
          });
          return;
        }
        updateData.CreditPriceWithoutTax = parseFloat(editForm.CreditPriceWithoutTax);
      }
      const hasSubscriptionPlansChanged = () => {
        const currentPlans = editForm.SubscriptionValidityInDays || {};
        const originalPlans = selectedPhysio?.SubscriptionValidityInDays || {};

        const allCountries = new Set([
          ...Object.keys(currentPlans),
          ...Object.keys(originalPlans),
        ]);

        for (const country of allCountries) {
          const currentCountryPlans = currentPlans[country] || [];
          const originalCountryPlans = originalPlans[country] || [];

          if (currentCountryPlans.length !== originalCountryPlans.length) {
            return true;
          }

          for (let i = 0; i < currentCountryPlans.length; i++) {
            const currentPlan = currentCountryPlans[i];
            const originalPlan = originalCountryPlans[i];

            if (
              currentPlan.Month !== originalPlan.Month ||
              currentPlan.Amount !== originalPlan.Amount ||
              currentPlan.AmountWithoutTax !== originalPlan.AmountWithoutTax
            ) {
              return true;
            }
          }
        }

        return false;
      };

      if (hasSubscriptionPlansChanged()) {
        updateData.SubscriptionValidityInDays = calculateDaysForPlans(
          editForm.SubscriptionValidityInDays
        );
      }

      const hasChanges = Object.keys(updateData).length > 1;

      if (hasChanges) {
        console.log("Update API - Using UserID:", selectedPhysioId);
        const updateResult = await PhysioServices.updatePhysioDetails(
          token,
          navigate,
          updateData
        );

        if (updateResult.status !== 200) {
          throw new Error("Failed to update details");
        }
      }

      if (hasCreditsData && hasChanges) {
        toast.success("Credits assigned and details updated successfully!", {
          position: "bottom-left",
          autoClose: 3000,
        });
      } else if (hasCreditsData) {
        toast.success("Credits assigned successfully!", {
          position: "bottom-left",
          autoClose: 3000,
        });
      } else if (hasChanges) {
        toast.success("Details updated successfully!", {
          position: "bottom-left",
          autoClose: 3000,
        });
      } else {
        toast.info("No changes to save", {
          position: "bottom-left",
          autoClose: 2000,
        });
        return;
      }

      setIsEditMode(false);
      setOpenDetailsDialog(false);
      setCreditsForm({
        Credits: "",
        ResellerID: "",
        SalesExecutiveID: "",
        AmountWithoutTax: "",
        IsPaid: false,
      });
      setSalesExecutiveList([]);
      fetchPhysioList();
    } catch (error) {
      toast.error(error.message, {
        position: "bottom-left",
        autoClose: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSubscriptionValidityChange = (country, index, field, value) => {
    if (field === "Month") {
      const existingPlans = editForm.SubscriptionValidityInDays[country] || [];
      const isDuplicate = existingPlans.some(
        (plan, i) => i !== index && plan.Month === value
      );

      if (isDuplicate) {
        toast.error(`Month ${value} already exists in ${country} plans!`, {
          position: "bottom-left",
          autoClose: 3000,
        });
        return;
      } else {
        setMonthValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`${country}-${index}`];
          return newErrors;
        });
      }
    }

    setEditForm((prev) => ({
      ...prev,
      SubscriptionValidityInDays: {
        ...prev.SubscriptionValidityInDays,
        [country]:
          prev.SubscriptionValidityInDays[country]?.map((item, i) => {
            if (i === index) {
              const updatedItem = { ...item, [field]: value };
              if (field === "Month") {
                updatedItem.Days = value * 30;
              }
              return updatedItem;
            }
            return item;
          }) || [],
      },
    }));
  };

  const addSubscriptionPlan = (country) => {
    const existingPlans = editForm.SubscriptionValidityInDays[country] || [];
    const existingMonths = existingPlans.map((plan) => plan.Month);

    let nextMonth = 1;
    while (existingMonths.includes(nextMonth)) {
      nextMonth++;
    }

    const newPlan = {
      Days: nextMonth * 30,
      Month: nextMonth,
      Amount: 0,
      AmountWithoutTax: 0,
    };
    setEditForm((prev) => ({
      ...prev,
      SubscriptionValidityInDays: {
        ...prev.SubscriptionValidityInDays,
        [country]: [
          ...(prev.SubscriptionValidityInDays[country] || []),
          newPlan,
        ],
      },
    }));

    setTimeout(() => {
      const element = document.getElementById(
        `plan-${country}-${
          (editForm.SubscriptionValidityInDays[country] || []).length
        }`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const removeSubscriptionPlan = (country, index) => {
    setEditForm((prev) => ({
      ...prev,
      SubscriptionValidityInDays: {
        ...prev.SubscriptionValidityInDays,
        [country]:
          prev.SubscriptionValidityInDays[country]?.filter(
            (_, i) => i !== index
          ) || [],
      },
    }));
  };

  const renderMainContent = () => (
    <div className={classes.viewContainer}>
      <div className={classes.infoGrid}>
        <div className={classes.basicInfoCard}>
          <div className={classes.cardHeader}>
            <div className={classes.headerTitle}>
              <h3>Basic Information</h3>
              <button
                className={classes.editButton}
                onClick={() => setIsEditMode(!isEditMode)}
                title={isEditMode ? "Cancel Edit" : "Edit Details"}
              >
                <EditIcon />
                {isEditMode ? "Cancel" : "Edit"}
              </button>
            </div>
          </div>
          <div className={classes.infoList}>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Name:</span>
              <span className={classes.infoValue}>{selectedPhysio?.Name}</span>
            </div>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Email:</span>
              <span className={classes.infoValue}>{selectedPhysio?.Email}</span>
            </div>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Mobile:</span>
              <span className={classes.infoValue}>
                {selectedPhysio?.Mobile_Number === -1
                  ? "-"
                  : selectedPhysio?.Mobile_Number}
              </span>
            </div>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Status:</span>
              {isEditMode ? (
                <div className={classes.selectContainer}>
                  <select
                    className={classes.selectField}
                    value={editForm.Status}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, Status: e.target.value }))
                    }
                  >
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Onboarding">Onboarding</option>
                  </select>
                </div>
              ) : (
                <span
                  className={`${classes.statusChip} ${
                    classes[selectedPhysio?.Status?.toLowerCase()]
                  }`}
                >
                  {selectedPhysio?.Status}
                </span>
              )}
            </div>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Credit Price (₹):</span>
              {isEditMode ? (
                <input
                  type="number"
                  value={editForm.CreditPriceWithoutTax}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty value or positive numbers only (greater than 0)
                    if (value === '' || (parseFloat(value) > 0 && !isNaN(parseFloat(value)))) {
                      setEditForm((prev) => ({
                        ...prev,
                        CreditPriceWithoutTax: value === '' ? '' : parseFloat(value),
                      }));
                    }
                  }}
                  className={classes.smallInput}
                  placeholder="Enter price"
                />
              ) : (
                <span className={classes.infoValue}>
                  {selectedPhysio?.CreditPriceWithoutTax && selectedPhysio?.CreditPriceWithoutTax > 0 ? selectedPhysio?.CreditPriceWithoutTax : "-"}
                </span>
              )}
            </div>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Active:</span>
              {isEditMode ? (
                <label className={classes.switchLabel}>
                  <input
                    type="checkbox"
                    className={classes.switchInput}
                    checked={editForm.IsActive}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        IsActive: e.target.checked,
                      }))
                    }
                  />
                  <span className={classes.switchSlider}></span>
                  <span className={classes.switchText}>
                    {editForm.IsActive ? "Yes" : "No"}
                  </span>
                </label>
              ) : (
                <span
                  className={`${classes.statusChip} ${
                    selectedPhysio?.IsActive ? classes.active : classes.inactive
                  }`}
                >
                  {selectedPhysio?.IsActive ? "Yes" : "No"}
                </span>
              )}
            </div>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Delete User:</span>
              {isEditMode ? (
                <label className={classes.switchLabel}>
                  <input
                    type="checkbox"
                    className={classes.switchInput}
                    checked={editForm.IsDeleted}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        IsDeleted: e.target.checked,
                      }))
                    }
                  />
                  <span className={classes.switchSlider}></span>
                  <span className={classes.switchText}>
                    {editForm.IsDeleted ? "Yes" : "No"}
                  </span>
                </label>
              ) : (
                <span
                  className={`${classes.statusChip} ${
                    selectedPhysio?.IsDeleted
                      ? classes.active
                      : classes.inactive
                  }`}
                >
                  {selectedPhysio?.IsDeleted ? "Yes" : "No"}
                </span>
              )}
            </div>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>Show Subscription:</span>
              {isEditMode ? (
                <label className={classes.switchLabel}>
                  <input
                    type="checkbox"
                    className={classes.switchInput}
                    checked={editForm.ShowSubscription}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        ShowSubscription: e.target.checked,
                      }))
                    }
                  />
                  <span className={classes.switchSlider}></span>
                  <span className={classes.switchText}>
                    {editForm.ShowSubscription ? "Yes" : "No"}
                  </span>
                </label>
              ) : (
                <span
                  className={`${classes.statusChip} ${
                    selectedPhysio?.ShowSubscription
                      ? classes.active
                      : classes.inactive
                  }`}
                >
                  {selectedPhysio?.ShowSubscription ? "Yes" : "No"}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={classes.documentCard}>
          <div className={classes.cardHeader}>
            <h3>Document</h3>
          </div>
          <div className={classes.documentContainer}>
            {selectedPhysio?.Document ? (
              <img
                src={selectedPhysio.Document}
                alt="Physio Document"
                className={classes.documentImage}
              />
            ) : (
              <div className={classes.documentPlaceholder}>
                <svg
                  className={classes.placeholderIcon}
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <p className={classes.placeholderText}>No document uploaded</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {((!isEditMode && !selectedPhysio?.ShowSubscription) ||
        (isEditMode && !editForm.ShowSubscription)) && (
        <div className={classes.creditsSection}>
          <div className={classes.sectionHeader}>
            <h3>Assign Credits</h3>
            <p className={classes.creditsDescription}>
              Assign credits to this physiotherapist (* indicates mandatory
              fields)
            </p>
          </div>
          <div className={classes.creditsCard}>
            <div className={classes.creditsForm}>
              <div className={classes.formRow}>
                <div className={classes.inputGroup}>
                  <label className={classes.fieldLabel}>
                    Credits *{" "}
                    <span className={classes.helperText}>
                      (1 credit = 1 month)
                    </span>
                  </label>
                  <input
                    type="number"
                    value={creditsForm.Credits}
                    onChange={(e) =>
                      setCreditsForm((prev) => ({
                        ...prev,
                        Credits: e.target.value,
                      }))
                    }
                    className={classes.textInput}
                    placeholder="Enter number of credits"
                  />
                </div>
              </div>

              <div className={classes.formRow}>
                <div className={classes.inputGroup}>
                  <label className={classes.fieldLabel}>
                    Amount Without Tax *{" "}
                    <span className={classes.helperText}>
                      (This value will be visible to admins only)
                    </span>
                  </label>
                  <input
                    type="number"
                    value={creditsForm.AmountWithoutTax}
                    onChange={(e) =>
                      setCreditsForm((prev) => ({
                        ...prev,
                        AmountWithoutTax: e.target.value,
                      }))
                    }
                    className={classes.textInput}
                    placeholder="Enter amount without tax"
                  />
                </div>
                <div className={classes.switchField}>
                  <label className={classes.switchLabel}>
                    <input
                      type="checkbox"
                      className={classes.switchInput}
                      checked={creditsForm.IsPaid}
                      onChange={(e) =>
                        setCreditsForm((prev) => ({
                          ...prev,
                          IsPaid: e.target.checked,
                        }))
                      }
                    />
                    <span className={classes.switchSlider}></span>
                    <span className={classes.switchText}>Is Paid</span>
                  </label>
                </div>
              </div>

              <div className={classes.formRow}>
                <div className={classes.inputGroup}>
                  <label className={classes.fieldLabel}>Reseller</label>
                  <select
                    value={creditsForm.ResellerID}
                    onChange={(e) => handleResellerChange(e.target.value)}
                    className={`${classes.selectField} ${
                      !creditsForm.ResellerID ? classes.placeholderSelect : ""
                    }`}
                    disabled={isLoadingDropdowns}
                  >
                    <option value="">Select Reseller</option>
                    {resellerList.map((reseller) => (
                      <option
                        key={reseller.ResellerUserID}
                        value={reseller.ResellerUserID}
                      >
                        {reseller.BuisnessName} ({reseller.Email})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={classes.formRow}>
                <div className={classes.inputGroup}>
                  <label className={classes.fieldLabel}>Sales Executive</label>
                  <div
                    className={`${classes.selectWrapper} ${
                      !creditsForm.ResellerID
                        ? classes.selectWrapperDisabled
                        : ""
                    }`}
                    onClick={() => {
                      if (!creditsForm.ResellerID) {
                        toast.error("Please select a Reseller first", {
                          position: "bottom-left",
                          autoClose: 5000,
                        });
                      }
                    }}
                  >
                    <select
                      value={creditsForm.SalesExecutiveID}
                      onChange={(e) =>
                        setCreditsForm((prev) => ({
                          ...prev,
                          SalesExecutiveID: e.target.value,
                        }))
                      }
                      className={`${classes.selectField} ${
                        !creditsForm.SalesExecutiveID
                          ? classes.placeholderSelect
                          : ""
                      }`}
                      disabled={isLoadingDropdowns || !creditsForm.ResellerID}
                    >
                      <option value="">Select Sales Executive</option>
                      {salesExecutiveList.map((executive) => (
                        <option
                          key={executive.SalesExecutiveUserID}
                          value={executive.SalesExecutiveUserID}
                        >
                          {executive.BuisnessName} ({executive.Email})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Plans Section */}
      {((!isEditMode && selectedPhysio?.ShowSubscription) ||
        (isEditMode && editForm.ShowSubscription)) && (
        <div className={classes.subscriptionSection}>
          <div className={classes.sectionHeader}>
            <h3>Subscription Plans</h3>
          </div>

          {isEditMode ? (
            // Edit mode - show editable subscription plans
            <div className={classes.subscriptionEditGrid}>
              {Object.entries(editForm.SubscriptionValidityInDays).map(
                ([country, plans]) => (
                  <div key={country} className={classes.subscriptionEditCard}>
                    <div className={classes.planHeader}>
                      <h4>{country} Plans</h4>
                      <button
                        className={classes.addPlanButton}
                        onClick={() => addSubscriptionPlan(country)}
                      >
                        <AddIcon />
                        Add Plan
                      </button>
                    </div>

                    <div className={classes.plansEditList}>
                      {plans.map((plan, index) => (
                        <div
                          key={index}
                          id={`plan-${country}-${index}`}
                          className={`${classes.planEditItem} ${
                            hasZeroAmounts(plan) ? classes.planWithWarning : ""
                          }`}
                        >
                          {hasZeroAmounts(plan) && (
                            <div className={classes.warningBanner}>
                              <WarningIcon className={classes.warningIcon} />
                              <span className={classes.warningText}>
                                Zero Amount!
                              </span>
                            </div>
                          )}
                          <div className={classes.planInputs}>
                            <div className={classes.inputGroup}>
                              <label>Months</label>
                              <input
                                type="number"
                                value={plan.Month}
                                onChange={(e) =>
                                  handleSubscriptionValidityChange(
                                    country,
                                    index,
                                    "Month",
                                    parseInt(e.target.value)
                                  )
                                }
                                className={`${classes.numberInput} ${
                                  monthValidationErrors[`${country}-${index}`]
                                    ? classes.errorInput
                                    : ""
                                }`}
                              />
                              {monthValidationErrors[`${country}-${index}`] && (
                                <span className={classes.errorText}>
                                  {monthValidationErrors[`${country}-${index}`]}
                                </span>
                              )}
                            </div>
                            <div className={classes.inputGroup}>
                              <label>Amount</label>
                              <input
                                type="number"
                                value={plan.Amount}
                                onChange={(e) =>
                                  handleSubscriptionValidityChange(
                                    country,
                                    index,
                                    "Amount",
                                    parseFloat(e.target.value)
                                  )
                                }
                                className={classes.numberInput}
                              />
                            </div>
                            <div className={classes.inputGroup}>
                              <label>Amount (No Tax)</label>
                              <input
                                type="number"
                                value={plan.AmountWithoutTax}
                                onChange={(e) =>
                                  handleSubscriptionValidityChange(
                                    country,
                                    index,
                                    "AmountWithoutTax",
                                    parseFloat(e.target.value)
                                  )
                                }
                                className={classes.numberInput}
                              />
                            </div>
                          </div>
                          <button
                            className={classes.removePlanButton}
                            onClick={() =>
                              removeSubscriptionPlan(country, index)
                            }
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            // View mode - show read-only subscription plans
            selectedPhysio?.SubscriptionValidityInDays &&
            Object.keys(selectedPhysio.SubscriptionValidityInDays).length >
              0 && (
              <div className={classes.subscriptionPlanRow}>
                {Object.entries(selectedPhysio.SubscriptionValidityInDays).map(
                  ([country, plans]) => (
                    <div key={country} className={classes.subscriptionPlanCard}>
                      <div className={classes.planHeader}>
                        <h4>{country} Plans</h4>
                        <span className={classes.planCount}>
                          {plans.length} plans
                        </span>
                      </div>
                      <div className={classes.plansList}>
                        {plans.map((plan, index) => (
                          <div key={index} className={classes.planItem}>
                            <div className={classes.planDetails}>
                              <div className={classes.planField}>
                                <span className={classes.fieldLabel}>
                                  Duration:
                                </span>
                                <span className={classes.fieldValue}>
                                  {plan.Month}{" "}
                                  {plan.Month === 1 ? "month" : "months"}
                                </span>
                              </div>
                              <div className={classes.planField}>
                                <span className={classes.fieldLabel}>
                                  Amount:
                                </span>
                                <span className={classes.fieldValue}>
                                  {country === "US"
                                    ? "$"
                                    : country === "IN"
                                    ? "₹"
                                    : country === "CA"
                                    ? "$"
                                    : country === "AE"
                                    ? "AED "
                                    : ""}
                                  {plan.Amount}
                                </span>
                              </div>
                              <div className={classes.planField}>
                                <span className={classes.fieldLabel}>
                                  Amount (No Tax):
                                </span>
                                <span className={classes.fieldValue}>
                                  {country === "US"
                                    ? "$"
                                    : country === "IN"
                                    ? "₹"
                                    : country === "CA"
                                    ? "$"
                                    : country === "AE"
                                    ? "AED "
                                    : ""}
                                  {plan.AmountWithoutTax}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    if (!isDataFetched && !isFetchingRef.current) {
      console.log("Calling fetchPhysioList from useEffect");
      fetchPhysioList();
    }
  }, [isDataFetched, fetchPhysioList]);

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 70,
    },
    {
      field: "Name",
      headerName: "Name",
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}>
            {params.value?.charAt(0)?.toUpperCase()}
          </Avatar>
          {params.value}
        </Box>
      ),
    },
    {
      field: "Email",
      headerName: "Email",
      width: 300,
    },
    {
      field: "Mobile_Number",
      headerName: "Mobile",
      width: 160,
      renderCell: (params) => (
        <span>{params.value === -1 ? "-" : params.value}</span>
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      width: 160,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "Approved"
              ? "success"
              : params.value === "Rejected"
              ? "error"
              : "warning"
          }
          size="small"
          sx={{
            backgroundColor:
              params.value === "Approved"
                ? "#e8f5e9"
                : params.value === "Rejected"
                ? "#ffebee"
                : "#fff3e0",
            color:
              params.value === "Approved"
                ? "#2e7d32"
                : params.value === "Rejected"
                ? "#c62828"
                : "#e65100",
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      field: "IsActive",
      headerName: "Active",
      width: 120,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {params.value ? (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#e8f5e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2e7d32",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✓
            </Box>
          ) : (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#ffebee",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d32f2f",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✗
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "ShowSubscription",
      headerName: "Show Sub",
      width: 140,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {params.value ? (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#e8f5e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2e7d32",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✓
            </Box>
          ) : (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#ffebee",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#d32f2f",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✗
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            startIcon={<ViewIcon />}
            onClick={() => fetchPhysioDetails(params.row.id)}
            variant="outlined"
          >
            View
          </Button>
          <Button
            size="small"
            startIcon={<CalendarIcon />}
            onClick={() => handleSlotsAction(params.row.id, params.row.Name)}
            variant="outlined"
          >
            Slots
          </Button>
        </Box>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{ fileName: "PhysioList" }}
          excelOptions={{ fileName: "PhysioList" }}
        />
      </GridToolbarContainer>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: 2 }}>
        <Link underline="hover" color="inherit" href={BASE_PATH_TO_HOME}>
          Dashboard
        </Link>
        <Typography color="text.primary">Physio Services</Typography>
        <Typography color="text.primary">Physiotherapists</Typography>
      </Breadcrumbs>

      <DataGridPremium
        className={classes.dataGrid}
        slots={{
          toolbar: CustomToolbar,
        }}
        rows={data}
        columns={columns}
        loading={isLoading}
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        columnSpacing={60}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
      />

      {/* Physio Details Dialog with Custom Tabs */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => {
          setOpenDetailsDialog(false);
          setSelectedPhysioId(null);
          setMonthValidationErrors({});
          setIsEditMode(false);
          setCreditsForm({
            Credits: "",
            ResellerID: "",
            SalesExecutiveID: "",
            AmountWithoutTax: "",
            IsPaid: false,
          });
          setSalesExecutiveList([]);
          setResellerList([]);
        }}
        maxWidth="lg"
        fullWidth
        className={classes.modernDialog}
        PaperProps={{
          className: classes.modernDialogPaper,
        }}
      >
        {/* Custom Header */}
        <div className={classes.modernHeader}>
          <div className={classes.headerContent}>
            <div className={classes.physioInfo}>
              <div className={classes.physioAvatar}>
                {selectedPhysio?.Name?.charAt(0)?.toUpperCase()}
              </div>
              <div className={classes.physioDetails}>
                <h2 className={classes.physioName}>{selectedPhysio?.Name}</h2>
                <p className={classes.physioEmail}>{selectedPhysio?.Email}</p>
              </div>
            </div>
            <button
              className={classes.closeButton}
              onClick={() => {
                setOpenDetailsDialog(false);
                setSelectedPhysioId(null);
                setMonthValidationErrors({});
                setIsEditMode(false);
                setCreditsForm({
                  Credits: "",
                  ResellerID: "",
                  SalesExecutiveID: "",
                  AmountWithoutTax: "",
                  IsPaid: false,
                });
                setSalesExecutiveList([]);
                setResellerList([]);
              }}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={classes.tabContent}>{renderMainContent()}</div>

        {/* Custom Footer */}
        <div className={classes.modernFooter}>
          <button
            className={classes.cancelButton}
            onClick={() => {
              setOpenDetailsDialog(false);
              setSelectedPhysioId(null);
              setMonthValidationErrors({});
              setIsEditMode(false);
              setCreditsForm({
                Credits: "",
                ResellerID: "",
                SalesExecutiveID: "",
                AmountWithoutTax: "",
                IsPaid: false,
              });
              setSalesExecutiveList([]);
              setResellerList([]);
            }}
          >
            Close
          </button>
          <button
            className={`${classes.updateButton} ${
              !hasAnyChanges() ? classes.disabledButton : ""
            }`}
            onClick={updatePhysioDetails}
            disabled={isUpdating || !hasAnyChanges()}
          >
            {isUpdating ? (
              <>
                <CircularProgress size={16} style={{ marginRight: "8px" }} />
                Processing...
              </>
            ) : (
              "Update Details"
            )}
          </button>
        </div>
      </Dialog>
    </Box>
  );
};

export default PhysioList;
